import React, { useState } from 'react';
import { Eye, EyeOff, TrendingUp, Users, MessageCircle, BarChart3, Instagram, Globe, Mail, Lock, ArrowLeft, ArrowRight, DoorClosed, Calculator, FileText, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

export const Login: React.FC = () => {
  const [view, setView] = useState<'login' | 'forgot_password'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login } = useAuth();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const sanitized = value.replace(/[^a-zA-Z0-9@.\-_]/g, '');
    
    if (value !== sanitized) {
      toast.warning('Caracteres especiais (como acentos ou espaços) não são permitidos no e-mail.');
    }
    
    setEmail(sanitized);
  };

  // Detecção de domínio do Método ACM / Evolua Negócios
  const isAcmDomain = typeof window !== 'undefined' && (
    window.location.hostname.includes('evoluanegocios') ||
    window.location.hostname.includes('crm.evoluanegocios.com') ||
    window.location.hostname.includes('metodoacm') ||
    window.location.search.includes('brand=acm') ||
    window.location.search.includes('brand=evolua') ||
    window.location.search.includes('niche=acm')
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    if (view === 'forgot_password') {
      try {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (resetError) throw resetError;
        toast.success('Link de recuperação enviado para o seu e-mail!');
        setView('login');
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Erro ao solicitar recuperação.';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      await login(email, password);
      // Força o redirecionamento para o dashboard
      window.location.href = '/';
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Ocorreu um erro ao fazer login.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const defaultFeatures = [
    {
      icon: TrendingUp,
      title: 'Gestão de Vendas',
      description: 'Controle completo do funil e das suas oportunidades'
    },
    {
      icon: Users,
      title: 'Gestão de Clientes',
      description: 'Organização da carteira e histórico de atendimentos'
    },
    {
      icon: MessageCircle,
      title: 'Chat & WhatsApp',
      description: 'Comunicação ágil e integrada com seus clientes'
    },
    {
      icon: BarChart3,
      title: 'Relatórios Comerciais',
      description: 'Métricas de fechamento e análises de desempenho'
    }
  ];

  const acmFeatures = [
    {
      icon: Users,
      title: 'Gestão de Clientes & Negociações',
      description: 'Funil de vendas completo para acompanhar todos os seus clientes'
    },
    {
      icon: DoorClosed,
      title: 'Orçamentos de Portas em ACM',
      description: 'Configuração ágil com modelos, medidas de vão, cores de chapa e acabamentos'
    },
    {
      icon: MessageCircle,
      title: 'Atendimento Integrado no WhatsApp',
      description: 'Centralize conversas, envie mensagens rápidas e acelere o fechamento'
    },
    {
      icon: FileText,
      title: 'Propostas & Acompanhamento de Pedidos',
      description: 'Gere propostas em PDF e acompanhe cada pedido desde o orçamento até a entrega'
    }
  ];

  const features = isAcmDomain ? acmFeatures : defaultFeatures;

  return (
    <div className="min-h-screen bg-slate-950 flex font-sans">
      {/* Left Side - Brand & Features */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-5/12 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden flex-col justify-between p-12 xl:p-16 border-r border-slate-800/60 isolate">
        
        {/* Glow de Fundo Elegante */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-yellow-600/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        {/* Top Branding Section */}
        <div className="relative">
          <div className="flex items-center gap-4 mb-12">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 flex items-center justify-center">
                <img src="/logo-orion.svg" alt="Orion Logo" className="w-11 h-11 object-contain drop-shadow-[0_0_12px_rgba(196,154,69,0.3)]" />
              </div>
              <div>
                <h1 className="text-3xl font-extrabold text-white tracking-tight leading-none">
                  Orion CRM
                </h1>
                <span className="text-xs font-semibold text-blue-400 tracking-wide">
                  {isAcmDomain ? 'Método ACM • Evolua Negócios' : 'Gestão Comercial Inteligente'}
                </span>
              </div>
            </div>
          </div>

          {/* Hero Headlines */}
          <div className="space-y-5 mb-12">
            <h2 className="text-4xl xl:text-5xl font-extrabold text-white leading-tight tracking-tight">
              {isAcmDomain ? (
                <>
                  Gestão comercial e <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-yellow-200 to-blue-300">
                    atendimento de clientes.
                  </span>
                </>
              ) : (
                <>
                  Transforme sua gestão <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-yellow-400">
                    de vendas e clientes.
                  </span>
                </>
              )}
            </h2>
            <p className="text-slate-300 text-base xl:text-lg leading-relaxed max-w-lg">
              {isAcmDomain
                ? 'O CRM completo para organizar suas negociações, gerar orçamentos de portas em ACM e acompanhar seus clientes desde o primeiro contato até a entrega.'
                : 'A plataforma completa para impulsionar seus resultados comerciais, organizar seu funil e gerenciar sua equipe.'}
            </p>
          </div>

          {/* Features List */}
          <div className="space-y-5">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="flex items-start gap-4 p-4 rounded-xl bg-slate-900/40 border border-slate-800/60 backdrop-blur-sm transition-all hover:bg-slate-800/40 hover:border-slate-700/60"
              >
                <div className="w-11 h-11 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0 mt-0.5">
                  <feature.icon className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative pt-8 border-t border-slate-800/60 mt-8 flex items-center justify-between text-xs text-slate-500">
          <p>
            {isAcmDomain
              ? '© 2026 Orion CRM • Em parceria com Evolua Negócios.'
              : '© 2025 Orion CRM. Todos os direitos reservados.'}
          </p>
          <div className="flex items-center gap-3">
            <a
              href={isAcmDomain ? "https://evoluanegocios.com" : "https://qualiteevidence.com.br"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-[44px] h-[44px] rounded-lg bg-slate-800/60 hover:bg-slate-700 hover:text-white transition-colors"
              title="Site Oficial"
              aria-label="Site oficial"
            >
              <Globe size={14} />
            </a>
            <a
              href={isAcmDomain ? "https://instagram.com/evoluanegocios" : "https://www.instagram.com/qualiteseguros.oficial/"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-[44px] h-[44px] rounded-lg bg-slate-800/60 hover:bg-slate-700 hover:text-white transition-colors"
              title="Instagram"
              aria-label="Instagram"
            >
              <Instagram size={14} />
            </a>
          </div>
        </div>
      </div>

      {/* Right Side - Clean Login Form */}
      <div className="w-full lg:w-1/2 xl:w-7/12 flex items-center justify-center p-6 sm:p-12 bg-slate-50 dark:bg-slate-950 relative transition-colors isolate overflow-hidden">
        
        {/* Ambient Glow Effects for Right Side */}
        <div className="absolute inset-0 pointer-events-none -z-10">
          <div className="absolute -top-20 -right-20 w-[600px] h-[600px] bg-blue-500/10 dark:bg-blue-600/10 rounded-full blur-[120px]"></div>
          <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] bg-yellow-500/10 dark:bg-yellow-600/10 rounded-full blur-[100px]"></div>
        </div>

        <div className="w-full max-w-[550px] -mt-20 flex flex-col">
          
          {/* Logo Outside the Card for ACM Domain */}
          {isAcmDomain && (
            <div className="flex justify-center mb-8">
              <img 
                src="/orion-acm-light.svg" 
                alt="Orion CRM - Evolua Negócios" 
                className="w-[340px] sm:w-[460px] h-auto object-contain dark:hidden"
                onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
              />
              <img 
                src="/orion-acm-dark.svg" 
                alt="Orion CRM - Evolua Negócios" 
                className="w-[340px] sm:w-[460px] h-auto object-contain hidden dark:block"
                onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
              />
            </div>
          )}

          {/* Glassmorphism Card */}
          <div className="w-full bg-white/70 dark:bg-slate-900/40 backdrop-blur-2xl border border-white/50 dark:border-slate-700/50 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] dark:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] rounded-[2.5rem] p-8 sm:p-14">
            
            {/* Mobile Header / Brand */}
            <div className={`mb-8 text-center ${isAcmDomain ? '' : 'lg:text-left'}`}>
              {!isAcmDomain && (
                <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <img src="/logo-orion.svg" alt="Orion Logo" className="w-10 h-10 object-contain drop-shadow-[0_0_10px_rgba(196,154,69,0.2)]" />
                  </div>
                  <div className="text-left">
                    <h2 className="text-xl font-black text-slate-900 dark:text-white leading-none">
                      Orion CRM
                    </h2>
                  </div>
                </div>
              )}

              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                {view === 'forgot_password' ? 'Recuperar Senha' : 'Acesse sua Conta'}
              </h2>
              <p className="text-base text-slate-500 dark:text-slate-400 mt-2">
              {view === 'forgot_password'
                ? 'Informe seu e-mail corporativo para receber as instruções de recuperação.'
                : 'Insira suas credenciais para gerenciar clientes, orçamentos e vendas.'}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50 rounded-xl flex items-start gap-3 text-red-700 dark:text-red-300 text-sm">
              <span className="shrink-0 mt-0.5">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                E-mail corporativo
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">
                  <Mail size={22} />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={handleEmailChange}
                  className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 text-base focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all hover:bg-slate-100/70 dark:hover:bg-slate-900 focus:bg-white dark:focus:bg-slate-900 shadow-sm"
                  placeholder="nome@empresa.com"
                  required
                />
              </div>
            </div>

            {view === 'login' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Senha de acesso
                  </label>
                  <button
                    type="button"
                    onClick={() => { setView('forgot_password'); setError(null); }}
                    className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline transition-colors inline-flex items-center min-h-[44px] -my-[13px] px-1 -mx-1"
                  >
                    Esqueceu a senha?
                  </button>
                </div>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">
                    <Lock size={22} />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-4 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 text-base focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all hover:bg-slate-100/70 dark:hover:bg-slate-900 focus:bg-white dark:focus:bg-slate-900 shadow-sm"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    aria-pressed={showPassword}
                    className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-[44px] h-[44px] text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                  </button>
                </div>
              </div>
            )}

            <div className="pt-4 space-y-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full min-h-[44px] py-4 px-6 bg-yellow-600 hover:bg-yellow-500 text-slate-950 rounded-xl text-base font-bold shadow-[0_4px_14px_0_rgba(202,138,4,0.39)] hover:shadow-[0_6px_20px_rgba(202,138,4,0.23)] hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-slate-950/30 border-t-slate-950 rounded-full animate-spin"></div>
                    <span>Acessando...</span>
                  </>
                ) : (
                  <>
                    <span>{view === 'forgot_password' ? 'Enviar Link de Recuperação' : 'Entrar no Sistema'}</span>
                    {view !== 'forgot_password' && <ArrowRight size={18} />}
                  </>
                )}
              </button>

              {view === 'forgot_password' && (
                <button
                  type="button"
                  onClick={() => { setView('login'); setError(null); }}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors text-sm font-semibold"
                >
                  <ArrowLeft size={16} />
                  <span>Voltar para o Login</span>
                </button>
              )}
            </div>
          </form>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;